<template>
  <form @submit.prevent="handleSubmit" class="side-wrapper side-wrapper-flex">
    <!-- Header -->
    <div class="side-header">
      <div class="side-wrapper-subtitle">{{ generalStore.viewHeader }}</div>
      <h1 v-if="form.id">{{ $gettext("Edit partner") }} '{{ form.name }}'</h1>
      <h1 v-else>{{ $gettext("Create partner") }}</h1>
    </div>

    <!-- Body -->
    <div class="side-body">
      <!-- Name -->
      <NiceInput :title="$gettext('Name')" v-model="form.name" type="text" :required="true" placeholder="Janez" />

      <!-- Contact name -->
      <NiceInput :title="$gettext('Contact name')" v-model="form.contact_name" type="text" placeholder="Janez Novak" />

      <!-- Contact email -->
      <NiceInput :title="$gettext('Contact email')" v-model="form.contact_email" type="email" placeholder="janez@olaii.com" />

      <!-- Guest group -->
      <NiceDropdown :title="$gettext('Guest group')" v-model="form.guest_group" :searchFunction="searchGroups" valueName="name" :required="true" />

      <!-- Invite link guest limit -->
      <NiceInput :title="$gettext('Invite link guest limit')" v-model="form.invite_link_guest_limit" type="number" placeholder="0" />

      <!-- Invite link expire at -->
      <NiceDate :title="$gettext('Invite link expire at')" v-model="form.invite_link_expire_at" :required="true" />

      <!-- Invite link show guests -->
      <NiceSwitch :title="$gettext('Show submitted guests on invite link')" v-model="form.invite_link_show_guests" />

      <!-- Invite link show guest limit -->
      <NiceSwitch :title="$gettext('Show guest limit to partner')" v-model="form.invite_link_show_guest_limit" />

      <!-- Invite link show non required fields -->
      <NiceSwitch :title="$gettext('Invite link show non required fields')" v-model="form.invite_link_show_non_required_fields" />

      <!-- Require guest confirmation -->
      <NiceSwitch :title="$gettext('Require guest confirmation')" v-model="form.require_guest_confirmation" />

      <!-- Invite link allow PDF download -->
      <NiceSwitch :title="$gettext('Invite link allow PDF download')" v-model="form.invite_link_allow_pdf_download" />

      <!-- <pre>{{ form }}</pre> -->
    </div>

    <!-- Footer -->
    <div class="side-footer">
      <NiceActions @cancel="cancel" :loading="loading" :showDelete="!!form.id" @delete="handleDelete" />
    </div>
  </form>
</template>

<script setup>
import { defineProps, onMounted, defineEmits, ref, computed } from "vue";
import { clone } from "@/plugins/query";
import { sub } from "date-fns";

import { useGettext } from "vue3-gettext";
const { $gettext } = useGettext();

import { useRouter } from "vue-router";
const router = useRouter();

import { useGeneralStore } from "@/store/general.store";
const generalStore = useGeneralStore();
const event = ref(generalStore.event);

import ApiService from "@/services/api/api.service";
import CommonService from "@/services/common.service";
import ErrorService from "@/services/error.service";

const emit = defineEmits(["cancel", "success"]);
const props = defineProps({
  data: {
    type: Object,
    required: false,
  },
});
const defaultExpireTime = computed(() => sub(event.value.start_time, { days: 1 }));
const form = ref({
  name: null,
  contact_name: null,
  contact_email: null,
  guest_group: null,
  invite_link_guest_limit: null,
  invite_link_expire_at: defaultExpireTime,
  invite_link_show_guests: false,
  invite_link_show_guest_limit: false,
  invite_link_show_non_required_fields: false,
  require_guest_confirmation: false,
  invite_link_allow_pdf_download: false,
});
const loading = ref(false);
const organizationId = router.currentRoute.value.params.organizationId;
const eventId = router.currentRoute.value.params.eventId;

async function handleSubmit() {
  loading.value = true;
  try {
    if (form.value.id) {
      form.value = await ApiService.partners.update(organizationId, eventId, form.value);
      CommonService.success($gettext("Partner was updated"));
    } else {
      form.value = await ApiService.partners.create(organizationId, eventId, form.value);
      CommonService.success($gettext("Partner was created"));
    }
    emit("success");
  } catch (error) {
    ErrorService.showError(error);
  } finally {
    loading.value = false;
  }
}

async function handleDelete() {
  loading.value = true;
  try {
    form.value = await ApiService.partners.delete(organizationId, eventId, form.value.id);
    CommonService.success($gettext("Partner was deleted"));
    emit("success");
  } catch (error) {
    ErrorService.showError(error);
  } finally {
    loading.value = false;
  }
}

function cancel() {
  emit("cancel");
}

async function searchGroups(keyword) {
  return ApiService.groups.getAll(organizationId, eventId, { search: keyword });
}

onMounted(async () => {
  const partnersId = router.currentRoute.value.query.id;
  if (partnersId == "new") return;
  if (props.data) return (form.value = clone(props.data));

  try {
    form.value = await ApiService.partners.get(organizationId, eventId, partnersId);
  } catch (e) {
    emit("cancel");
  }
});
</script>
